import { useNavigate } from 'react-router-dom';

export default function VisualGrid() {
  const navigate = useNavigate();

  const learningPaths = [
    {
      id: 'k12',
      title: 'K-12 Grade Level Mathematics',
      description: 'Complete curriculum aligned with Common Core, State, and National Standards',
      icon: 'ri-book-open-line',
      image: 'https://public.readdy.ai/ai/img_res/002b67eab95e1730b6b65d9e5ad696e8.jpg',
      gradient: 'from-blue-500 to-indigo-600',
      size: 'large'
    },
    {
      id: 'test-prep',
      title: 'Standardized Test Preparation',
      description: 'SAT, ACT, GRE, GMAT, State Tests, AP Exams, and more',
      icon: 'ri-file-list-3-line',
      image: 'https://readdy.ai/api/search-image?query=Professional%20standardized%20test%20preparation%20materials%20with%20answer%20sheets%20pencils%20and%20calculator%20on%20a%20clean%20white%20surface%20with%20soft%20lighting%20and%20academic%20atmosphere%20showing%20excellence%20and%20success&width=800&height=600&seq=test-prep-grid-001&orientation=landscape',
      gradient: 'from-emerald-500 to-teal-600',
      size: 'medium'
    },
    {
      id: 'books',
      title: 'Original Mathematics Books',
      description: 'Exclusive textbooks and test prep guides by Augustine Anyimadu-Ahenkae',
      icon: 'ri-book-2-line',
      image: 'https://readdy.ai/api/search-image?query=Stack%20of%20professional%20mathematics%20textbooks%20with%20elegant%20covers%20on%20a%20clean%20white%20background%20with%20soft%20shadows%20and%20premium%20publishing%20quality%20showing%20academic%20excellence%20and%20authority&width=800&height=600&seq=books-grid-001&orientation=landscape',
      gradient: 'from-amber-500 to-orange-600',
      size: 'medium'
    },
    {
      id: 'free',
      title: 'Free Lessons & Videos',
      description: 'Sample our teaching style with complimentary resources',
      icon: 'ri-video-line',
      image: 'https://readdy.ai/api/search-image?query=Modern%20video%20player%20interface%20showing%20mathematics%20lesson%20on%20a%20tablet%20screen%20with%20play%20button%20and%20educational%20graphics%20on%20a%20clean%20white%20surface%20with%20professional%20lighting%20and%20engaging%20learning%20atmosphere&width=800&height=600&seq=free-grid-001&orientation=landscape',
      gradient: 'from-purple-500 to-pink-600',
      size: 'medium'
    }
  ];

  const handleCardClick = (id: string) => {
    if (id === 'k12') {
      navigate('/grades');
      return;
    }

    const sectionMap: Record<string, string> = {
      'test-prep': 'learning-system',
      'books': 'books-section',
      'free': 'free-section'
    };
    
    const targetId = sectionMap[id];
    const element = document.getElementById(targetId);
    element?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="py-24 px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-5xl md:text-6xl font-bold mb-6 text-gray-900">
            Choose Your Learning Path
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Select from our comprehensive mathematics programs designed to help you master every concept with clarity and confidence
          </p>
        </div>

        {/* Grid Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {learningPaths.map((path, index) => (
            <div
              key={path.id}
              className={`group relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 cursor-pointer hover:scale-105 ${
                index === 0 ? 'md:col-span-2' : ''
              }`}
              onClick={() => handleCardClick(path.id)}
              style={{ height: index === 0 ? '500px' : '400px' }}
            >
              {/* Background Image */}
              <div className="absolute inset-0 w-full h-full">
                <img
                  src={path.image}
                  alt={path.title}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
                />
              </div>

              {/* Gradient Overlay */}
              <div className={`absolute inset-0 bg-gradient-to-br ${path.gradient} opacity-80 group-hover:opacity-90 transition-opacity duration-500`}></div>

              {/* Content */}
              <div className="relative h-full flex flex-col justify-end p-10 text-white">
                <div className="w-20 h-20 flex items-center justify-center mb-6">
                  <i className={`${path.icon} text-6xl`}></i>
                </div>
                <h3 className="text-3xl md:text-4xl font-bold mb-4 leading-tight">
                  {path.title}
                </h3>
                <p className="text-lg md:text-xl font-light leading-relaxed mb-6">
                  {path.description}
                </p>
                <div className="flex items-center gap-3 text-lg font-semibold">
                  <span>Explore Now</span>
                  <i className="ri-arrow-right-line text-2xl group-hover:translate-x-2 transition-transform duration-300"></i>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}